import React, { useEffect, useMemo, useState } from 'react';
import { SmallTablet } from '@shared/ui/atoms/DeviceTypes';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { WallPlannerDatesRow } from "@features/wall-planner/pages/components/WallPlannerDatesRow";
import { WorkPlannerContainer } from '@shared/ui/molecules/work-planner/WorkPlannerStructure';
import { WallPlannerUserRow } from "@features/wall-planner/pages/components/WallPlannerUserRow";
import { useDispatch, useSelector } from "react-redux";
import { HolidayViewerDropdownList } from "./components/HolidayViewerDropdownList";
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { INDIRECT_LINE_REPORT_ID, LINE_REPORT_ID, MonthlyHolidayView } from '@features/holidays/services/HolidayService';
import { HolidayGrouping } from "../models/Holidays.model";
import moment from "moment";
import { currentUserLineReports, selectCurrentUser } from '@shared/store/auth.duck';
import { loadTeamHolidays, selectIsLoading, selectMonthlyHolidayView } from '@features/holidays/store/holidays-v2.duck';
import { usePageName } from '@shared/hooks/UsePageName';
import isMobile from "is-mobile";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import styled from "styled-components";
import { selectAllTeams, selectConfig } from '@shared/store/config.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import { GroupType } from '@shared/models/team.models';
import { PageTitle } from '@shared/ui/atoms/PageTitle';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { WithWeeksInCurrentCalendarMonth } from '@features/whereabouts/hooks/WithWeekInCurrentCalendarMonth';
import { WorkPlannerLayout } from '@shared/ui/molecules/work-planner/WorkPlannerLayout';
import { Permission } from '@shared/models/user.models';
import { WithUsersLineReports } from '@shared/hooks/WithUsersLineReports';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { safeParseInt } from '@shared/utils/MathUtils';
import { PRETTY_SHORTER_DATE_FORMAT } from '@shared/utils/DateUtils';
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import { isInTeams } from '@shared/utils/TeamsUtils';
import { app } from "@microsoft/teams-js";
import { useLocation } from "react-router";

interface SelectedOption {
  id: string | number;
  groupType: GroupType;
}

function GroupHolidayToWallPlannerActivityConverter(groupings: HolidayGrouping[]): any[] {
  if (!groupings) return []
  return groupings.map(g => ({
    ...g,
    toDate: moment(g.toDate),
    fromDate: moment(g.fromDate),
    name: '',
  }))
}

export const SELECTED_GROUP_ID = 'selectedGroupId';

export function TeamsMonthlyHolidayView(props: Props) {
  const dispatch = useDispatch();
  const teams = useSelector(selectAllTeams);
  const workGroups = useSelector(selectAllTags);
  const departments = useSelector(selectAllDepartments);
  const calendarDate = useSelector(selectCalendarDate);
  const config = useSelector(selectConfig);
  const currentUser = useSelector(selectCurrentUser);
  const teamHolidays: MonthlyHolidayView[] = useSelector(selectMonthlyHolidayView);
  const customWhereaboutsList = useSelector(selectAllWhereaboutsOptions);
  const isLoading = useSelector(selectIsLoading);
  const weeksInMonth = WithWeeksInCurrentCalendarMonth();
  const {lineReports} = WithUsersLineReports(currentUser);
  const isHolidayTrackingEnabled = currentUser?.companyEntity.enableHolidayBooking;

  usePageName('Team Today - Team holidays');

  const [selectedOption, setSelectedOption] = useState<SelectedOption | undefined>(undefined);

  const onTeamSelect = async (selectedOptionId: string | number, groupType: GroupType) => {
    setSelectedOption({id: selectedOptionId, groupType: groupType});
    localStorage.setItem(SELECTED_GROUP_ID, `${selectedOptionId}::${groupType}`);
  }

  const enrichedTeamHolidays = useMemo(() => {
    return teamHolidays.map(th => {
      const enrichedCustomDays = th.customLeaveDays.map(cld => ({
        ...cld,
        amImage: customWhereaboutsList.find(cw => cw.id === cld.amWhereaboutsId)?.image,
        pmImage: customWhereaboutsList.find(cw => cw.id === cld.pmWhereaboutsId)?.image,
      }));
      const enrichedCustomNonWorkingDays = th.customNonWorkingDays.map(cld => ({
        ...cld,
        amImage: customWhereaboutsList.find(cw => cw.id === cld.amWhereaboutsId)?.image,
        pmImage: customWhereaboutsList.find(cw => cw.id === cld.pmWhereaboutsId)?.image,
      }));
      return {
        ...th,
        customLeaveDays: enrichedCustomDays,
        customNonWorkingDays: enrichedCustomNonWorkingDays,
      }
    })
  }, [customWhereaboutsList, teamHolidays]);

  useEffect(() => {
    dispatch(currentUserLineReports());
  }, [dispatch]);

  const usersTeamId = currentUser?.teamEntity?.id;

  useEffect(() => {
    if (selectedOption) {
      return;
    }

    try {
      const selectedGroup: any[] = (localStorage.getItem(SELECTED_GROUP_ID) || '').split('::');
      if (selectedGroup.length === 2) {
        const groupId = selectedGroup[0];
        const groupType = safeParseInt(selectedGroup[1]);
        if (groupType === GroupType.WorkGroup && workGroups.find(w => w.id === parseInt(groupId))) {
          setSelectedOption({id: parseInt(groupId), groupType: GroupType.WorkGroup});
          return;
        } else if (groupType === GroupType.Team && teams.find(t => t.id === groupId)) {
          setSelectedOption({id: groupId, groupType: GroupType.Team});
          return;
        } else if (groupType === GroupType.Department && departments.find(d => d.id === parseInt(groupId))) {
          setSelectedOption({id: parseInt(groupId), groupType: GroupType.Department});
          return;
        }
      } else {
        if (teams.find(t => usersTeamId === t.id)) {
          setSelectedOption({id: usersTeamId, groupType: GroupType.Team});
        }
      }
    } catch (e) {
      localStorage.removeItem(SELECTED_GROUP_ID);
    }
  }, [teams, workGroups, departments, usersTeamId, selectedOption]);

  useEffect(() => {
    if (selectedOption) {
      if (selectedOption.groupType === GroupType.Team) {
        dispatch(loadTeamHolidays({
          teamIds: [selectedOption.id],
          calendarDate: calendarDate
        }));
      } else if (selectedOption.groupType === GroupType.WorkGroup) {
        dispatch(loadTeamHolidays({
          workGroupIds: [selectedOption.id],
          calendarDate: calendarDate
        }));
      } else if (selectedOption.id === LINE_REPORT_ID) {
        dispatch(loadTeamHolidays({
          teamIds: [LINE_REPORT_ID],
          calendarDate: calendarDate
        }));
      } else if (selectedOption.id === INDIRECT_LINE_REPORT_ID) {
        dispatch(loadTeamHolidays({
          teamIds: [INDIRECT_LINE_REPORT_ID],
          calendarDate: calendarDate
        }));
      } else if (selectedOption.groupType === GroupType.Department) {
        dispatch(loadTeamHolidays({
          departmentIds: [selectedOption.id],
          calendarDate: calendarDate
        }));
      }
    }
  }, [dispatch, calendarDate, selectedOption]);

  const location = useLocation();
  useEffect(() => {
    if (isInTeams()) {
      app.getContext().then((context: app.Context) => {
        if (context.page.subPageId) {
          const parts = context.page.subPageId.split(':');
          if (parts[0] === 'approvals') {
            window.location.href = `/holidays/view-approvals`;
          }
        }
      });
    }
  }, [location, dispatch]);

  const addHolidayUrl = useMemo(() => {
    if (lineReports.length > 0) {
      return '/holidays/add'
    }

    if (currentUser?.role === Permission.Basic || currentUser?.role === Permission.TeamAdmin) {
      return `/holidays/${currentUser.id}/add`
    } else {
      return '/holidays/add'
    }

  }, [currentUser, lineReports]);

  const viewingHolidayYear = useMemo(() => {
    const start = config.getHolidayYearStartDate(calendarDate);
    const end = config.getHolidayYearEndDate(calendarDate);
    return `${start.format(PRETTY_SHORTER_DATE_FORMAT)} - ${end?.format(PRETTY_SHORTER_DATE_FORMAT)}`
  }, [config, calendarDate]);

  return (
    <>
      <WorkPlannerLayout pageContentStyles={{paddingTop: 24}} numberOfWeeksInMonth={weeksInMonth.length+1}>
        <HeaderRow style={{paddingTop: 0}}>
          <PageTitle text={'header.leave-planner'} style={{marginTop: 0}} subText={viewingHolidayYear} />
          <SmallTablet style={{marginBottom: 16, padding: '0 16px'}}>
            <DateFlicker dateScale={DateScale.Month} updateGlobalDate={true} />
          </SmallTablet>

          {isHolidayTrackingEnabled && <OutlineButton link={addHolidayUrl}
                                                      text={"button.add-holiday-request"}
                                                      fullWidth={true}
                                                      style={{maxWidth: 200}} />}
        </HeaderRow>
        <WorkPlannerContainer id={'workPlannerContainer'}>
          {isLoading && <LoadingWrapper>
            <LoadingSpinner fullScreen={false} />
          </LoadingWrapper>}

          <WallPlannerDatesRow weeksInMonth={weeksInMonth}/>
          {!isMobile() &&
            <WallPlannerUserRow>
              <HolidayViewerDropdownList teams={teams}
                                         workGroups={workGroups}
                                         departments={departments}
                                         onSelect={onTeamSelect}
                                         selectedOptionId={selectedOption ? selectedOption?.id : undefined} />
            </WallPlannerUserRow>
          }

          {enrichedTeamHolidays.map((monthView: MonthlyHolidayView, key: number) => (
            <WallPlannerUserRow key={key}
                                user={monthView.user}
                                working={[]}
                                holidays={GroupHolidayToWallPlannerActivityConverter(monthView.holidayGroupings)}
                                nonWorkingDays={monthView.nonWorkingDays}
                                customNonWorkingDays={monthView.customNonWorkingDays}
                                customLeaveDays={monthView.customLeaveDays}
                                companyHolidays={monthView.companyHolidays}
                                isLeaveManagementView={true} />
          ))}

          {/* Padding at the bottom */}
          <WallPlannerUserRow id={'workPlannerRow'} />
          <WallPlannerUserRow />
          <WallPlannerUserRow />
          <WallPlannerUserRow />
          <WallPlannerUserRow />
        </WorkPlannerContainer>
      </WorkPlannerLayout>
    </>
  )
}

interface Props {
}

const LoadingWrapper = styled.div`
  z-index: 99999;
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const HeaderRow = styled.div`
  margin-bottom: 16px;
  @media(${tablet}) {
    display: flex;
    justify-content: space-between;
    .dropdown__container {
      max-width: 300px;
    }
  }
`
